const AUTH_REDIRECT_MODES = new Set(['login', 'signup']);
const PENDING_GROUP_INVITE_KEY = 'kalonmoves:pending-group-invite';
const GROUP_INVITE_PATH = /^\/join\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function groupInvitePath(value: string | null): string | null {
  return value?.length === 42 && GROUP_INVITE_PATH.test(value) ? value : null;
}

export function savePendingGroupInvite(path: string | null): void {
  const destination = groupInvitePath(path);
  if (!destination) return;
  try {
    sessionStorage.setItem(PENDING_GROUP_INVITE_KEY, destination);
  } catch {
    // The current URL remains usable when browser storage is unavailable.
  }
}

export function loadPendingGroupInvite(): string | null {
  try {
    return groupInvitePath(sessionStorage.getItem(PENDING_GROUP_INVITE_KEY));
  } catch {
    return null;
  }
}

export function clearPendingGroupInvite(): void {
  try {
    sessionStorage.removeItem(PENDING_GROUP_INVITE_KEY);
  } catch {
    // Best effort when browser storage is unavailable.
  }
}

/** Administration has its own role and tenant guards, independent of health onboarding. */
export function isAdministrationPath(path: string | null): boolean {
  return path === '/admin' || path === '/admin/rbac' || path === '/company-admin';
}

/** Account controls stay reachable; Vault remains an employee health surface. */
export function isPersonalAccountPath(path: string, search: string): boolean {
  return path === '/profile' && new URLSearchParams(search).get('section') !== 'vault';
}

export function requiresCompanyMembership(path: string, search: string): boolean {
  if (isPersonalAccountPath(path, search)) return false;
  return ['/dashboard', '/coach', '/performance', '/rewards', '/leaderboard', '/anamnesebogen', '/anamnesebogen-direct', '/onboarding', '/join', '/profile']
    .some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/** Only explicit internal administration and group-invite destinations are accepted. */
export function resolvePostAuthRedirect(search: string): string {
  const requested = new URLSearchParams(search).get('redirect');
  if (requested && isAdministrationPath(requested)) return requested;
  return groupInvitePath(requested) ?? loadPendingGroupInvite() ?? '/dashboard';
}

export function buildInviteAuthRedirect(search: string): string {
  const params = new URLSearchParams(search);
  const sanitizedParams = new URLSearchParams();
  const invite = params.get('invite')?.trim().toUpperCase();
  const mode = params.get('mode')?.trim().toLowerCase();

  if (invite) {
    sanitizedParams.set('invite', invite);
  }

  if (mode && AUTH_REDIRECT_MODES.has(mode)) {
    sanitizedParams.set('mode', mode);
  }

  const sanitizedSearch = sanitizedParams.toString();
  return sanitizedSearch ? `/auth?${sanitizedSearch}` : '/auth';
}
